import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useResponsiveDesign } from '../../../responsiveDesign/responsiveDesign';
import { AssetIcon } from '../icon/AssetIcon';

export enum ButtonVariant {
  primary = 'primary',
  secondary = 'secondary',
  ghost = 'ghost',
  danger = 'danger',
  round = 'round',
  /**
   * Hyperlink-style button: transparent background, themed text color,
   * no padding/border, no underline, no size-based min-width/height.
   * Used for inline tappable text inside paragraphs (e.g. "Support" link in
   * `ContactSupportSection`). The `sizeStyles[size]` spread is skipped for
   * this variant so the button flows with surrounding text.
   */
  text = 'text',
}

export enum ButtonSize {
  small = 'sm',
  medium = 'md',
  large = 'lg',
}

export enum ButtonIconPosition {
  left = 'left',
  right = 'right',
  center = 'center',
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: string;
  iconColor?: string;
  /** Override the size-derived icon dimensions. When omitted, falls back to the size-based defaults. */
  iconSize?: number;
  backgroundColor?: string;
  textColor?: string;
  iconPosition?: ButtonIconPosition;
  loading?: boolean;
  children?: React.ReactNode;
}

export const ExpauseButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    variant = ButtonVariant.primary,
    size = ButtonSize.medium,
    icon,
    iconColor,
    iconSize,
    backgroundColor,
    textColor,
    iconPosition = ButtonIconPosition.left,
    loading = false,
    children,
    className = '',
    disabled,
    style,
    ...props
  }, ref) => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();

    const isRound = variant === ButtonVariant.round;
    const isText = variant === ButtonVariant.text;
    const isCentered = iconPosition === ButtonIconPosition.center;

    const baseClasses = isRound
      ? 'inline-flex items-center justify-center transition-opacity focus-visible:outline-none disabled:pointer-events-none rounded-full'
      : 'inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 rounded-lg';

    const variantClasses: Record<ButtonVariant, string> = {
      [ButtonVariant.primary]: 'hover:opacity-90',
      [ButtonVariant.secondary]: 'hover:opacity-80',
      [ButtonVariant.ghost]: 'hover:opacity-80',
      [ButtonVariant.danger]: '',
      [ButtonVariant.round]: 'hover:opacity-80',
      [ButtonVariant.text]: 'hover:opacity-80',
    };

    const sizeStyles: Record<ButtonSize, React.CSSProperties> = {
      [ButtonSize.small]: {
        height: responsive.button.size.large,
        padding: responsive.button.padding.medium,
        fontSize: responsive.font.base,
      },
      [ButtonSize.medium]: {
        height: responsive.button.size.large,
        minWidth: responsive.button.size.sl2,
        maxWidth: `20%`,
        fontSize: responsive.font.lg,
      },
      [ButtonSize.large]: {
        width: '100%',
        height: responsive.button.size.large,
        fontSize: responsive.font.lg,
      },
    };

    const sizeToRoundDiameter: Record<ButtonSize, number> = {
      [ButtonSize.small]: responsive.icon.medium * 2,
      [ButtonSize.medium]: responsive.icon.large * 2,
      [ButtonSize.large]: responsive.icon.large * 2.5,
    };

    const iconSizeForRound: Record<ButtonSize, number> = {
      [ButtonSize.small]: responsive.icon.small,
      [ButtonSize.medium]: responsive.icon.medium,
      [ButtonSize.large]: responsive.icon.large,
    };

    const iconSizePx: Record<ButtonSize, number> = {
      [ButtonSize.small]: responsive.icon.xxs,
      [ButtonSize.medium]: responsive.icon.xxs,
      [ButtonSize.large]: responsive.icon.xxs,
    };

    const getVariantStyle = (): React.CSSProperties => {
      switch (variant) {
        case ButtonVariant.primary:
          return {
            backgroundColor: backgroundColor || theme.button.primary,
            color: textColor || theme.white,
            // Longhand paddingLeft/Right (not shorthand `padding`) so the
            // horizontal breathing room survives being spread before
            // `sizeStyles[size]`; guarantees text never sits flush to the edge.
            paddingLeft: responsive.spacing.lg,
            paddingRight: responsive.spacing.lg,
          };
        case ButtonVariant.secondary:
          return {
            backgroundColor: backgroundColor || theme.button.secondary,
            color: textColor || theme.white,
            paddingLeft: responsive.spacing.lg,
            paddingRight: responsive.spacing.lg,
            borderWidth: `${responsive.border.thin}px`,
            borderStyle: 'solid',
            borderColor: theme.border,
          };
        case ButtonVariant.ghost:
          return { color: textColor || theme.text.primary, backgroundColor: backgroundColor || theme.button.ghost };
        case ButtonVariant.danger:
          return { backgroundColor: theme.button.danger, color: textColor || theme.white };
        case ButtonVariant.round:
          return {
            width: sizeToRoundDiameter[size],
            height: sizeToRoundDiameter[size],
            backgroundColor: backgroundColor || `${theme.button.round}`,
            padding: 0,
            border: 'none',
            opacity: disabled ? 0.6 : 1,
          };
        case ButtonVariant.text:
          // Hyperlink-style: transparent background, themed text color, no
          // padding/border, no underline. Size-based min-width/height is
          // skipped via the `isText` branch on render so this button flows
          // with surrounding text.
          return {
            backgroundColor: 'transparent',
            color: textColor || theme.blue,
            padding: 0,
            border: 'none',
          };
        default:
          return {};
      }
    };

    if (isRound && icon) {
      const resolvedIconSize = iconSize ?? iconSizeForRound[size];
      return (
        <button
          ref={ref}
          className={`${baseClasses} ${variantClasses[variant]} ${className}`}
          style={{ ...getVariantStyle(), ...style }}
          disabled={disabled || loading}
          // `data-qa-*` test attributes pass through here via `...props` (the
          // component extends native button attributes) — call-sites spread
          // `{...qaAttr({ id: 'x' })}` directly; no dedicated qa prop is needed.
          {...props}
        >
          <AssetIcon path={icon} width={resolvedIconSize} height={resolvedIconSize} color={iconColor || theme.white} />
        </button>
      );
    }

    const resolvedNonRoundIconSize = iconSize ?? iconSizePx[size];

    return (
      <button
        ref={ref}
        className={`${baseClasses} ${variantClasses[variant]} ${className}`}
        style={{ ...getVariantStyle(), ...(isCentered || isText ? {} : sizeStyles[size]), ...style }}
        disabled={disabled || loading}
        // `data-qa-*` test attributes pass through here via `...props` (the
        // component extends native button attributes) — call-sites spread
        // `{...qaAttr({ id: 'x' })}` directly; no dedicated qa prop is needed.
        {...props}
      >
        {loading ? (
          <div className="animate-spin rounded-full border-2 border-white border-t-transparent"
            style={{
              width: responsive.icon.small,
              height: responsive.icon.small,
              marginRight: responsive.spacing.sm
            }} />
        ) : icon && iconPosition === ButtonIconPosition.left ? (
          <AssetIcon path={icon} width={resolvedNonRoundIconSize} height={resolvedNonRoundIconSize}
            color={iconColor || theme.white}
            style={{
              marginRight: responsive.spacing.sm
            }} />
        ) : null}
        {!isCentered && children}
        {icon && iconPosition === ButtonIconPosition.right && !loading ? (
          <AssetIcon path={icon} width={resolvedNonRoundIconSize} height={resolvedNonRoundIconSize}
            color={iconColor || theme.white}
            style={{ marginLeft: responsive.spacing.sm }} />
        ) : null}
        {icon && iconPosition === ButtonIconPosition.center && !loading ? (
          <AssetIcon path={icon} width={resolvedNonRoundIconSize} height={resolvedNonRoundIconSize}
            color={iconColor || theme.white} />
        ) : null}
      </button>
    );
  }
);

ExpauseButton.displayName = 'ExpauseButton';
